import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { NOTIFICATION_TYPE } from '../../../constants/common'
import { RENTAL_APPLICANT_ENTITY } from '../../../constants/entity'
import { MENU_RENTAL_PORTAL_ITEM } from '../../../constants/menu'
import {
  ROUTE_PATH_LIST_RENTAL_PORTAL,
  ROUTE_UI_SECTION_NAME_RENTAL_PORTAL,
} from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import { usePageBrowserTabTitle } from '../../../hooks'
import {
  isListViewTypeBoard,
  isListViewTypeWithPreview,
} from '../../../utils/listViewType'
import Button from '../../Button'
import { withDraftProviders } from '../../Draft'
import { FilterBar } from '../../Filter'
import { MultipleViewTypeListLayout } from '../../MultipleViewTypeList'
import { NotificationInline } from '../../Notification'
import { Page, PageActions } from '../../Page'
import { PageActionsButtonAddEntity } from '../../PageActionsButtonAddEntity'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import { PageHeader } from '../../PageHeader'
import {
  getApplicantProfileLink,
  getMockApplicantsPolicy,
} from '../../RentalPortalApplicant'
import { TableExportData } from '../../Table'
import { TablePagination } from '../../TablePagination'
import Text from '../../Text'
import {
  RENTAL_PORTAL_LIST_RESOURCE,
  useRentalPortalExport,
  useRentalPortalListPermissions,
  useRentalPortalSectionPermissions,
} from '../RentalPortal.hooks'
import { getRentalPortalSubsectionByPathname } from '../RentalPortal.utils'

import { RentalPortalApplicantDraftProvider } from './RentalPortalApplicantDraftProvider'
import { RentalPortalApplicantMultipleViewTypeListActiveItemPreview } from './RentalPortalApplicantMultipleViewTypeListActiveItemPreview'
import {
  APPLICANT_CREATE_ACTION_NAME,
  APPLICANT_DRAFT_PREFIX,
  APPLICANTS_FILTER_BAR_ITEMS_PROPS,
  QA_NAME_APPLICANTS_ADD_NEW,
  QA_NAME_APPLICANTS_CLEAR_FILTERS,
  QA_NAME_APPLICANTS_FIT_SORT_NOTICE,
  QA_NAME_APPLICANTS_PAGE,
  QA_NAME_APPLICANTS_PREVIEW,
  QA_NAME_APPLICANTS_REFRESH_ERROR,
  QA_NAME_APPLICANTS_RETRY,
} from './RentalPortalApplicants.constants'
import {
  useCreateRentalPortalApplicant,
  useRentalPortalApplicantsFilters,
  useRentalPortalApplicantsListingOptions,
  useRentalPortalApplicantsMultipleViewTypeList,
  useRentalPortalApplicantsTable,
  useRentalPortalApplicantsViewRegime,
} from './RentalPortalApplicants.hooks'
import {
  getApplicantItemActions,
  getApplicantsAvailableFilters,
  getApplicantsFilteredEmptyTextKey,
  getApplicantsPayloadAvailableFilters,
  getApplicantsRefreshErrorText,
  getApplicantViewRegimeOptions,
  getHasApplicantsFitOrdering,
  getIsApplicantActionOpen,
  getTranslatedApplicantsAvailableFilters,
} from './RentalPortalApplicants.utils'
import { RentalPortalApplicantsPreviewEmpty } from './RentalPortalApplicantsPreviewEmpty'
import { RentalPortalApplicantsTableLayout } from './RentalPortalApplicantsTableLayout'
import { getApplicantExportValue } from './RentalPortalApplicantsTableRow'

import * as styles from './RentalPortalApplicants.module.css'

import RefreshIcon from '../../../icons/refresh.svg'

function RentalPortalApplicants() {
  const { t } = useTranslation()

  const { pathname } = useLocation()

  const [modal, setModal] = useState(null)

  const listingOptions = useRentalPortalApplicantsListingOptions()

  const availableFilters = getTranslatedApplicantsAvailableFilters({
    availableFilters: getApplicantsAvailableFilters({ listingOptions, t }),
    t,
  })

  const { filterValues, handleClearFilters, handleSetFilterValues } =
    useRentalPortalApplicantsFilters({ availableFilters })

  const tablePage = useRentalPortalApplicantsTable({ filterValues })

  const {
    allItems,
    checkIsItemSelected,
    handleRefresh,
    handleSetPagination,
    handleSetPreview,
    handleUpdateConfig,
    hasAttention,
    hasMatch,
    hasStage,
    isLoading,
    isRefetching,
    items,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    preview,
    selectAllPages,
    selected,
    serverError,
  } = tablePage

  const { handleSetListViewType, handleShowStageInTable, listViewType } =
    useRentalPortalApplicantsViewRegime({
      filterValues,
      handleSetFilterValues,
      hasStage,
    })

  const isBoardRegime = isListViewTypeBoard(listViewType)

  const {
    activeItem,
    handleChangeFocusedRow,
    handleChangeViewType,
    handleClickRow,
    handleMouseDownRow,
    handlePreviewResizeEnd,
    handleResetViewTypeToPlainBody,
    isSplitAvailable,
  } = useRentalPortalApplicantsMultipleViewTypeList({
    handleReload: handleRefresh,
    handleSetPreview,
    handleSetView: handleSetListViewType,
    handleUpdateConfig,
    items: isBoardRegime ? allItems : items?.items,
    listViewType,
    preview,
  })

  const filterBarAvailableFilters = getApplicantsPayloadAvailableFilters({
    availableFilters,
    hasAttention,
    hasMatch,
  })

  const { canCreate } = useRentalPortalSectionPermissions({
    sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.applicants,
  })

  const { listPermissions } = useRentalPortalListPermissions({
    resource: RENTAL_PORTAL_LIST_RESOURCE.applicants,
  })

  const { handleExport, isExportAvailable } = useRentalPortalExport({
    checkIsItemSelected,
    columns: tablePage.columns,
    getValue: getApplicantExportValue,
    items: allItems,
    section: ROUTE_PATH_LIST_RENTAL_PORTAL.applicants,
    selectAllPages,
    selected,
  })

  const subsection = getRentalPortalSubsectionByPathname(pathname)

  usePageBrowserTabTitle({ isLoading, pageTitle: t(subsection.title) })

  function handleCloseModal() {
    setModal(null)
  }

  function handleReloadAfterCreate() {
    handleSetPagination({ ...pagination, page: 1 })
    handleRefresh()
  }

  const { handleOpenCreation } = useCreateRentalPortalApplicant({
    setModal,
    onClose: handleCloseModal,
    onReload: handleReloadAfterCreate,
  })

  // TODO: `rental_erp_handoff_enabled` has no read path on this payload
  const { rental_erp_handoff_enabled: hasHandoff } = getMockApplicantsPolicy()

  const navigate = useNavigate()

  function handleCallRowAction(action, item) {
    if (getIsApplicantActionOpen(action)) {
      navigate(getApplicantProfileLink(item.id))
    }
  }

  const hasApplicants = items?.items?.length > 0

  const hasActiveFilters = Object.keys(filterValues).length > 0

  const hasCreateRight = canCreate && permissions.can_manage

  const hasBodyError = Boolean(serverError) && !hasApplicants

  const hasStaleError = Boolean(serverError) && hasApplicants

  const breadcrumbs = [
    {
      id: MENU_RENTAL_PORTAL_ITEM.title,
      title: t(MENU_RENTAL_PORTAL_ITEM.title),
      to: `/${ROUTE_PATH_LIST_RENTAL_PORTAL.root}`,
    },
    { id: subsection.title, title: t(subsection.title) },
  ]

  const emptyStateActions = getEmptyStateActions({
    actionName: APPLICANT_CREATE_ACTION_NAME,
    isLoading,
    qaName: QA_NAME_APPLICANTS_ADD_NEW,
    shouldShowActions: hasCreateRight && !hasActiveFilters,
    text: t('RentalPortal.ApplicantAddNewBtnKey'),
    onClick: handleOpenCreation,
  })

  return (
    <MultipleViewTypeListLayout
      isEmptyItems={!hasApplicants}
      listViewType={listViewType}
      pageScrollWrapperRef={pageScrollWrapperRef}
      previewProps={{
        Component: RentalPortalApplicantMultipleViewTypeListActiveItemPreview,
        Empty: RentalPortalApplicantsPreviewEmpty,
        activeItem,
        canResize: true,
        componentProps: { onClose: handleResetViewTypeToPlainBody },
        emptyProps: {
          EntityIcon: subsection.EmptyStateIcon,
          text: t('RentalPortal.ApplicantPreviewEmptyStateKey'),
          onClosePreview: handleResetViewTypeToPlainBody,
        },
        qaName: QA_NAME_APPLICANTS_PREVIEW,
        resizeConfig: {
          initialWidth: preview?.size ?? undefined,
          onResizeEnd: handlePreviewResizeEnd,
        },
      }}
    >
      <ScreenContainerRef value={pageScrollWrapperRef}>
        <Page
          pagination={
            <TablePagination
              responsive
              disabled={isBoardRegime}
              isSkeleton={isLoading}
              value={pagination}
              onChange={handleSetPagination}
            />
          }
          qaName={QA_NAME_APPLICANTS_PAGE}
          scrollWrapperRef={pageScrollWrapperRef}
        >
          <PageHeader
            breadcrumbs={breadcrumbs}
            isLoading={isLoading}
            itemsCount={isBoardRegime ? undefined : pagination.count}
            title={t(subsection.title)}
          >
            <PageActions className={styles.pageActions}>
              {permissions.can_export || isLoading ? (
                <TableExportData
                  buttonProps={{
                    disabledTooltip: 'RentalPortal.ExportEmptyTooltipKey',
                  }}
                  containerRef={pageScrollWrapperRef}
                  disabled={!isExportAvailable}
                  isSkeleton={isLoading}
                  titleKey='RentalPortal.ApplicantExportBtnKey'
                  onClick={handleExport}
                />
              ) : null}
              {hasCreateRight ? (
                <PageActionsButtonAddEntity
                  isSkeleton={isLoading}
                  qaName={QA_NAME_APPLICANTS_ADD_NEW}
                  text={t('RentalPortal.ApplicantAddNewBtnKey')}
                  onClick={handleOpenCreation}
                />
              ) : null}
            </PageActions>
          </PageHeader>
          {hasApplicants || hasActiveFilters || isLoading || hasBodyError ? (
            <FilterBar
              hideFilterTemplateSkeleton
              responsive
              availableFilters={filterBarAvailableFilters}
              buttonsClassName={styles.filterBarButtons}
              canViewFilterTemplate={listPermissions.can_view_filter_template}
              className={styles.filterBar}
              entity={RENTAL_APPLICANT_ENTITY}
              filterBarItemsProps={{
                ...APPLICANTS_FILTER_BAR_ITEMS_PROPS,
                className: styles.filterBarItems,
                controlClassName: styles.filterBarControl,
              }}
              filterValues={filterValues}
              isSkeleton={isLoading}
              ordering={ordering}
              pagination={{ per_page: pagination.pageSize }}
              setFilterValues={handleSetFilterValues}
              onReload={handleRefresh}
            />
          ) : null}
          {getHasApplicantsFitOrdering(ordering) ? (
            <NotificationInline
              className={styles.fitSortNotice}
              qaName={QA_NAME_APPLICANTS_FIT_SORT_NOTICE}
              text={t('RentalPortal.MatchSortNoticeKey')}
              type={NOTIFICATION_TYPE.info}
            />
          ) : null}
          {hasStaleError ? (
            <NotificationInline
              actions={[
                {
                  beforeIconProps: { Icon: RefreshIcon },
                  callback: handleRefresh,
                  isLoading: isRefetching,
                  text: t('RentalPortal.ListingRetryBtnKey'),
                },
              ]}
              className={styles.refreshError}
              qaName={QA_NAME_APPLICANTS_REFRESH_ERROR}
              text={getApplicantsRefreshErrorText({
                availableFilters,
                filterValues,
                t,
              })}
              type={NOTIFICATION_TYPE.error}
            />
          ) : null}
          <RentalPortalApplicantsTableLayout
            activeItem={activeItem}
            arrowNavigationConfig={{
              disabled: !isListViewTypeWithPreview(listViewType),
              focusedRow: activeItem,
            }}
            changeViewTypeButtonProps={{
              hasBoard: hasStage,
              hasSplit: isSplitAvailable,
              listViewType,
              options: getApplicantViewRegimeOptions({
                hasStage,
                isSplitAvailable,
                listViewType,
              }),
              onChange: handleChangeViewType,
            }}
            emptyState={
              <PageEmptyState
                EntityIcon={subsection.EmptyStateIcon}
                actions={emptyStateActions}
                text={hasActiveFilters ? undefined : t(subsection.emptyTextKey)}
              >
                {hasActiveFilters ? (
                  <>
                    <Text isSecondary>
                      {t(getApplicantsFilteredEmptyTextKey(filterValues))}
                    </Text>
                    <Button.Flat
                      qaName={QA_NAME_APPLICANTS_CLEAR_FILTERS}
                      text={t('Table.ClearFilterBtnKey')}
                      onClick={handleClearFilters}
                    />
                  </>
                ) : null}
              </PageEmptyState>
            }
            errorState={
              <PageEmptyState EntityIcon={subsection.EmptyStateIcon}>
                <Text isSecondary>
                  {t('RentalPortal.ListingProfileApplicantsLoadErrorKey')}
                </Text>
                <Button.Flat
                  beforeIconProps={{ Icon: RefreshIcon }}
                  isLoading={isRefetching}
                  qaName={QA_NAME_APPLICANTS_RETRY}
                  text={t('RentalPortal.ListingRetryBtnKey')}
                  onClick={handleRefresh}
                />
              </PageEmptyState>
            }
            getApplicantItemActions={getApplicantItemActions}
            hasBodyError={hasBodyError}
            hasHandoff={hasHandoff}
            hasTable={
              hasApplicants ||
              isLoading ||
              hasBodyError ||
              isBoardRegime ||
              isListViewTypeWithPreview(listViewType)
            }
            listViewType={listViewType}
            tablePage={tablePage}
            onCallRowAction={handleCallRowAction}
            onChangeFocusedRow={handleChangeFocusedRow}
            onClickRow={handleClickRow}
            onMouseDownRow={handleMouseDownRow}
            onShowMore={handleShowStageInTable}
          />
          <ScreenContainerRef value={null}>{modal}</ScreenContainerRef>
        </Page>
      </ScreenContainerRef>
    </MultipleViewTypeListLayout>
  )
}

export default withDraftProviders(RentalPortalApplicants, [
  {
    Provider: RentalPortalApplicantDraftProvider,
    prefix: APPLICANT_DRAFT_PREFIX,
  },
])
